'use client';

import dynamic from 'next/dynamic';
import type { VideoBlock } from '@impact/types';
import { cn } from '@/lib/utils';

const PlyrVideoPlayer = dynamic(() => import('./PlyrVideoPlayer').then((m) => m.PlyrVideoPlayer), {
  ssr: false,
  loading: () => <div className="absolute inset-0 animate-pulse bg-muted/40" />,
});

interface VideoBlockRendererProps {
  block: VideoBlock;
  editing?: boolean;
}

const WIDTH_CLASSES = {
  full: 'w-full',
  wide: 'max-w-4xl mx-auto',
  medium: 'max-w-2xl mx-auto',
  small: 'max-w-md mx-auto',
} as const;

const ASPECT_CLASSES = {
  auto: 'aspect-video',
  video: 'aspect-video',
  square: 'aspect-square',
  '4/3': 'aspect-[4/3]',
  '9/16': 'aspect-[9/16]',
} as const;

function getVideoEmbed(
  url: string,
): { kind: 'iframe'; embedUrl: string; provider: 'youtube' | 'vimeo' } | { kind: 'native' } | null {
  if (!url) return null;

  const youtubeMatch = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
  );
  if (youtubeMatch) {
    return {
      kind: 'iframe',
      embedUrl: `https://www.youtube.com/embed/${youtubeMatch[1]}`,
      provider: 'youtube',
    };
  }

  const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeoMatch) {
    return {
      kind: 'iframe',
      embedUrl: `https://player.vimeo.com/video/${vimeoMatch[1]}`,
      provider: 'vimeo',
    };
  }

  // Direct video URL (mp4, webm, ogg, mov) or an uploaded video asset —
  // asset objectKeys always contain a `/tenant/{id}/video/` path segment.
  if (/\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i.test(url) || /\/tenant\/[^/]+\/video\//.test(url)) {
    return { kind: 'native' };
  }

  return null;
}

export function VideoBlockRenderer({ block, editing }: VideoBlockRendererProps) {
  const {
    url,
    caption,
    autoplay = false,
    muted = false,
    loop = false,
    controls = true,
    width = 'wide',
    aspectRatio = 'video',
    rounded = true,
    shadow = true,
    border = false,
    posterUrl,
  } = block.data;

  const videoInfo = getVideoEmbed(url);

  const frameClasses = cn(
    'relative overflow-hidden',
    ASPECT_CLASSES[aspectRatio],
    rounded && 'rounded-xl',
    shadow && 'shadow-lg',
    border && 'border border-border',
    !videoInfo && 'bg-muted/40',
  );

  if (!videoInfo) {
    return (
      <figure className={cn('my-8', WIDTH_CLASSES[width])}>
        <div className={frameClasses}>
          <div className="absolute inset-0 flex items-center justify-center px-6 text-center text-sm text-muted-foreground">
            {url
              ? 'Unable to load video. Provide a YouTube or Vimeo link, or upload a video file.'
              : editing
                ? 'Use the toolbar above to embed or upload a video'
                : 'No video provided.'}
          </div>
        </div>
        {(caption || editing) && (
          <figcaption
            className="mt-3 inline-block min-w-[4rem] text-center text-sm text-muted-foreground empty:before:text-muted-foreground/50 empty:before:content-['Caption']"
            {...(editing ? { 'data-editable': 'data.caption' } : {})}
          >
            {caption}
          </figcaption>
        )}
      </figure>
    );
  }

  let body: React.ReactNode;
  if (videoInfo.kind === 'iframe') {
    let embedUrl = videoInfo.embedUrl;
    const params = new URLSearchParams();

    if (autoplay && !editing) {
      params.set('autoplay', '1');
      params.set('muted', '1');
    }
    if (muted) params.set('muted', '1');
    if (loop) params.set('loop', '1');
    if (!controls) {
      if (videoInfo.provider === 'youtube') params.set('controls', '0');
      else params.set('background', '1');
    }

    if (videoInfo.provider === 'youtube') {
      params.set('rel', '0');
      params.set('modestbranding', '1');
    } else if (videoInfo.provider === 'vimeo') {
      params.set('dnt', '1');
    }

    const queryString = params.toString();
    if (queryString) embedUrl += `?${queryString}`;

    body = (
      <iframe
        src={embedUrl}
        title={caption || 'Video'}
        className="absolute inset-0 h-full w-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    );
  } else {
    const effectiveAutoplay = autoplay && !editing;
    body = (
      <div className="absolute inset-0 [&_.plyr]:h-full [&_.plyr]:w-full [&_video]:h-full [&_video]:w-full [&_video]:object-cover">
        <PlyrVideoPlayer
          src={url}
          poster={posterUrl}
          autoplay={effectiveAutoplay}
          muted={muted || effectiveAutoplay}
          loop={loop}
          controls={controls}
        />
      </div>
    );
  }

  return (
    <figure className={cn('my-8', WIDTH_CLASSES[width])}>
      <div className={frameClasses}>{body}</div>
      {(caption || editing) && (
        <figcaption
          className="mt-3 inline-block min-w-[4rem] text-center text-sm text-muted-foreground empty:before:text-muted-foreground/50 empty:before:content-['Caption']"
          {...(editing ? { 'data-editable': 'data.caption' } : {})}
        >
          {caption}
        </figcaption>
      )}
    </figure>
  );
}
